import { useEffect, useRef, useCallback } from 'react';

export interface UseWheelNavOptions {
  onNext: () => void;
  onPrev: () => void;
  threshold?: number;   // Delta accumulation threshold before turning page (default: 60)
  debounceMs?: number;  // Cooldown in ms between page turns (default: 300)
  enabled?: boolean;    // Whether wheel navigation is enabled (default: true)
}

/**
 * Debounced Mouse Wheel Navigation Hook with Threshold Protection (Feature 9)
 *
 * Captures vertical wheel delta, applies threshold gating to prevent
 * accidental micro-scrolls or momentum jitter, and enforces a debounce
 * cooldown period between consecutive page turns.
 */
export function useWheelNav({
  onNext,
  onPrev,
  threshold = 60,
  debounceMs = 300,
  enabled = true,
}: UseWheelNavOptions) {
  const accumulatedDelta = useRef(0);
  const lastTriggerTime = useRef(0);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!enabled) return;

      // Ignore horizontal-only scrolls
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) && Math.abs(e.deltaY) < 5) {
        return;
      }

      // Check if user is scrolling inside a scrollable drawer or modal container
      const target = e.target as HTMLElement | null;
      if (target && typeof target.closest === 'function') {
        // If target is inside an open drawer or modal content that is scrollable
        const scrollableParent = target.closest(
          '.drawer-content, .modal-content, .backlog-entries-list, .acts-accordion-list, textarea'
        );
        if (scrollableParent) {
          return;
        }

        // If any modal or drawer overlay is active, suppress page wheel navigation
        if (
          document.querySelector(
            '.drawer-overlay, .modal-overlay, .caesura-audio-modal-overlay'
          )
        ) {
          return;
        }
      }

      const now = Date.now();

      // Enforce debounce cooldown
      if (now - lastTriggerTime.current < debounceMs) {
        accumulatedDelta.current = 0;
        return;
      }

      // Reset accumulated delta if user paused scrolling
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
      resetTimer.current = setTimeout(() => {
        accumulatedDelta.current = 0;
      }, 150);

      accumulatedDelta.current += e.deltaY;

      // Threshold protection
      if (accumulatedDelta.current >= threshold) {
        // Scrolled down -> Next page
        accumulatedDelta.current = 0;
        lastTriggerTime.current = now;
        onNext();
      } else if (accumulatedDelta.current <= -threshold) {
        // Scrolled up -> Previous page
        accumulatedDelta.current = 0;
        lastTriggerTime.current = now;
        onPrev();
      }
    },
    [enabled, threshold, debounceMs, onNext, onPrev]
  );

  useEffect(() => {
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, [handleWheel]);
}

export default useWheelNav;
